import React, { useMemo } from 'react';

export const GridPreview = ({
  component,
  theme,
  runtime = false,
  onClick,
  isSelected,
  allComponents = [],
  renderChild
}) => {
  const { commonAttrs = {}, specificAttrs = {} } = component || {};

  const rows = Math.max(1, Math.min(6, Number(specificAttrs.rows || 1)));
  const cols = Math.max(1, Math.min(6, Number(specificAttrs.cols || 1)));
  const gap = Number(specificAttrs.gap ?? 12);
  const padding = Number(specificAttrs.padding ?? 16);

  const cells = Array.isArray(specificAttrs.cells) ? specificAttrs.cells : [];

  // Map componentId -> component object (so cells can reference a component by id)
  const componentIndex = useMemo(() => {
    const map = new Map();
    (allComponents || []).forEach((c) => {
      if (c?.id) map.set(c.id, c);
    });
    return map;
  }, [allComponents]);

  // Responsive columns (simple + stable)
  // - Desktop uses "cols"
  // - Tablet/mobile can override, or auto-fall back
  const tabletColsRaw = specificAttrs.responsive?.tabletCols ?? 'auto';
  const mobileColsRaw = specificAttrs.responsive?.mobileCols ?? 'auto';

  const tabletCols = tabletColsRaw === 'auto' ? Math.min(cols, 3) : Math.max(1, Math.min(6, Number(tabletColsRaw)));
  const mobileCols = mobileColsRaw === 'auto' ? 1 : Math.max(1, Math.min(6, Number(mobileColsRaw)));

  const shadow =
    specificAttrs.shadow === 'small'
      ? '0 1px 2px rgba(0,0,0,0.05)'
      : specificAttrs.shadow === 'medium'
      ? '0 4px 6px rgba(0,0,0,0.1)'
      : specificAttrs.shadow === 'large'
      ? '0 10px 15px rgba(0,0,0,0.15)'
      : 'none';

  const containerStyle = {
    padding: `${padding}px`,
    margin: `${commonAttrs.marginTop || 0}px ${commonAttrs.marginRight || 0}px ${commonAttrs.marginBottom || 0}px ${commonAttrs.marginLeft || 0}px`,
    backgroundColor: specificAttrs.backgroundColor || 'transparent',
    borderRadius: `${Number(specificAttrs.borderRadius || 4)}px`,
    boxShadow: shadow,
    //border: specificAttrs.borderColor ? `${Number(specificAttrs.borderWidth || 1)}px solid ${specificAttrs.borderColor}` : 'none',
    outline: isSelected ? '2px solid #3B82F6' : 'none',
    cursor: onClick ? 'pointer' : 'default',
    width: '100%',
    overflow: 'hidden',
    border: '2px solid black'
  };

  // We render grid in DOM with CSS variables + media queries (inline + <style> safe in React)
  const gridId = `mudumbai_grid_${component.id}`;

  const gridStyle = {
    display: 'grid',
    gap: `${gap}px`,
    width: '100%',
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`
  };

  const cellBorder = specificAttrs.showCellBorders && !runtime ? '1px dashed #E5E7EB' : '1px solid transparent';
  const cellIndex = useMemo(() => {
  const m = new Map();
  (cells || []).forEach((x) => {
    if (x?.row != null && x?.col != null) m.set(`${x.row}_${x.col}`, x);
  });
  return m;
}, [cells]);
  return (
    <div style={containerStyle} onClick={onClick}>
      {/* Responsive column overrides scoped to this component */}
      <style>
        {`
          @media (max-width: 1024px) {
            #${gridId} { grid-template-columns: repeat(${tabletCols}, minmax(0, 1fr)); }
          }
          @media (max-width: 640px) {
            #${gridId} { grid-template-columns: repeat(${mobileCols}, minmax(0, 1fr)); }
          }
        `}
      </style>

      <div id={gridId} style={gridStyle}>
        {Array.from({ length: rows * cols }).map((_, idx) => {
          const r = Math.floor(idx / cols) + 1;
          const c = (idx % cols) + 1;

          // Find matching cell config
          const cell = cellIndex.get(`${r}_${c}`);
          // fixed/updated by ChatGPT on 2026-01-25 14:30:00 – support rendering by componentType if no componentRefId is linked
        const refId = cell?.componentRefId || null;
        const referenced = refId ? componentIndex.get(refId) : null;

        const inlineType = !referenced ? (cell?.componentType || null) : null;
        const inlineComponent = inlineType
        ? { id: `${component.id}_${cell.id}`, type: inlineType, commonAttrs: {}, specificAttrs: {} }
        : null;

          return (
            <div
              key={`${r}_${c}`}
              style={{
                minHeight: '60px',
                border: cellBorder,
                borderRadius: '8px',
                padding: runtime ? '0' : '6px',
                boxSizing: 'border-box',
                overflow: 'hidden',
                display: 'flex'
              }}
              onClick={(e) => {
                // Allow selecting the grid (parent) without accidentally selecting children when in builder mode
                if (!runtime) e.stopPropagation();
              }}
            >
              {(referenced || inlineComponent) && typeof renderChild === 'function' ? (
                <div style={{ width: '100%' }}>
                  {renderChild(referenced || inlineComponent)}
                </div>
              ) : (
                !runtime && (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#9CA3AF',
                      fontSize: '11px',
                      textAlign: 'center',
                      padding: '8px'
                    }}
                  >
                    Empty
                    <div style={{ fontSize: '10px', marginTop: '4px' }}>
                      Row {r} • Col {c}
                    </div>
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};